import { autoUpdate, flip, offset, shift, useDismiss, useFloating, useHover, useInteractions } from "@floating-ui/react";
import { useContext, useEffect, useState } from "react";
import { Localization, SessionContext } from "@viamap/viamap2-common";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { floatingBoundaryMiddleware, MITFloatingPortal, useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { GlassButton } from "src/components/GlassButtons";
import { useANNEditorContext } from "./ANNEditorState";
import { AnnotationContextType, AnnotationType, type ANNAnnotationRecord } from "./ANNTypes";
import { downloadOverlayImage } from "./ANNImageOverlayStorage";
import { ANNManager } from "./ANNManager";
import { ExportData } from "src/managers/ExportData";

/**
 * Shows annotation count for a servitut tingbog row (same documentId as servitut details / ANN table).
 * Renders nothing while loading, on error, when the module is off, or when count is 0.
 */
export function ServitutAnnotationCountBadge(props: {
   documentId: string;
   datoloebenr?: string;
   downloadFileName?: string;
}) {
   const { documentId } = props;
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const { state: editorState } = useANNEditorContext();
   const { root } = useFloatingPortalContext();
   const [count, setCount] = useState<number | null>(null);
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const organizationId = sessionState?.customerRef;
   const moduleOn = hasAccessToFeature(Feature.AnnotationModule);
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: "top",
      open: tooltipOpen,
      middleware: [
         offset(5),
         ...(root != null ? floatingBoundaryMiddleware(root) : [flip({ padding: 8 }), shift({ padding: 8 })]),
      ],
      onOpenChange: setTooltipOpen,
   });
   const {
      getReferenceProps,
      getFloatingProps,
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);

   useEffect(() => {
      if (!documentId || !moduleOn) {
         setCount(null);
         return;
      }
      let cancelled = false;
      setCount(null);
      void (async () => {
         try {
            const n = await ANNManager.countServitutAnnotations(documentId, organizationId);
            if (!cancelled) {
               setCount(n);
            }
         } catch {
            if (!cancelled) {
               setCount(null);
            }
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [documentId, organizationId, moduleOn]);

   useEffect(() => {
      if (!editorState.lastSaveTimestamp) {
         return;
      }
      if (editorState.contextType !== AnnotationContextType.Servitut) {
         return;
      }
      if (String(editorState.identifier ?? "") !== documentId) {
         return;
      }
      if (!documentId || !moduleOn) {
         return;
      }
      let cancelled = false;
      const t = setTimeout(() => {
         void (async () => {
            try {
               const n = await ANNManager.countServitutAnnotations(documentId, organizationId);
               if (!cancelled) {
                  setCount(n);
               }
            } catch {
               if (!cancelled) {
                  setCount(null);
               }
            }
         })();
      }, 800);
      return () => {
         cancelled = true;
         clearTimeout(t);
      };
   }, [
      editorState.lastSaveTimestamp,
      editorState.contextType,
      editorState.identifier,
      documentId,
      organizationId,
      moduleOn,
   ]);

   if (!documentId || !moduleOn) {
      return null;
   }
   if (count === null || count < 1) {
      return null;
   }

   const locale = Localization.getLocale().toLowerCase();
   const isDanish = locale.startsWith("da");
   const annotationLabel = count === 1
      ? (isDanish ? "annotation" : "annotation")
      : (isDanish ? "annotationer" : "annotations");
   const title = `${count} ${annotationLabel}`;
   return (
      <>
         <span
            ref={refs.setReference}
            className="tingbog-servitut-annotation-count"
            aria-label={title}
            {...getReferenceProps()}
         >
            {count}
         </span>
         {tooltipOpen ? (
            <MITFloatingPortal>
               <div
                  ref={refs.setFloating}
                  className="GlassButtonToolTip"
                  style={floatingStyles}
                  {...getFloatingProps()}
               >
                  {title}
               </div>
            </MITFloatingPortal>
         ) : null}
      </>
   );
}

function normalizeAnnotationType(value?: string): string {
   return (value || "").trim().toLowerCase();
}

function normalizeDatoloebenr(value?: string): string {
   return (value || "").trim().toLowerCase();
}

function isPdfAnnotationDocument(item: ANNAnnotationRecord): boolean {
   const mimeType = (item.document?.mimeType || "").toLowerCase();
   if (mimeType.includes("pdf")) {
      return true;
   }
   const fileName = (item.document?.filename || "").toLowerCase();
   return fileName.endsWith(".pdf");
}

function getQuickDownloadCandidate(items: ANNAnnotationRecord[], datoloebenr?: string): ANNAnnotationRecord | null {
   const targetDatoloebenr = normalizeDatoloebenr(datoloebenr);
   return items.find((item) => {
      const normalizedType = normalizeAnnotationType(item.annotationType as string | undefined);
      const isMatchingDocumentAnnotation =
         item.type === AnnotationType.Document &&
         (normalizedType === "dokument" || normalizedType === "servitutdokument") &&
         Boolean(item.document?.s3Key) &&
         isPdfAnnotationDocument(item);
      if (!isMatchingDocumentAnnotation) {
         return false;
      }
      if (!targetDatoloebenr) {
         return true;
      }
      return normalizeDatoloebenr(item.datoloebenr) === targetDatoloebenr;
   }) || null;
}

export function ServitutAnnotationQuickDownloadButton(props: {
   documentId: string;
   datoloebenr?: string;
   downloadFileName?: string;
}) {
   const { documentId, datoloebenr, downloadFileName } = props;
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { state: sessionState } = useContext(SessionContext);
   const { state: editorState } = useANNEditorContext();
   const [quickDownloadRecord, setQuickDownloadRecord] = useState<ANNAnnotationRecord | null>(null);
   const [downloading, setDownloading] = useState(false);
   const organizationId = sessionState?.customerRef;
   const moduleOn = hasAccessToFeature(Feature.AnnotationModule);

   useEffect(() => {
      if (!documentId || !moduleOn) {
         setQuickDownloadRecord(null);
         return;
      }
      let cancelled = false;
      setQuickDownloadRecord(null);
      void (async () => {
         try {
            const items = await ANNManager.listAnnotations(AnnotationContextType.Servitut, documentId, organizationId);
            if (!cancelled) {
               setQuickDownloadRecord(getQuickDownloadCandidate(items, datoloebenr));
            }
         } catch {
            if (!cancelled) {
               setQuickDownloadRecord(null);
            }
         }
      })();
      return () => {
         cancelled = true;
      };
   }, [documentId, datoloebenr, organizationId, moduleOn]);

   useEffect(() => {
      if (!editorState.lastSaveTimestamp) {
         return;
      }
      if (editorState.contextType !== AnnotationContextType.Servitut) {
         return;
      }
      if (String(editorState.identifier ?? "") !== documentId) {
         return;
      }
      if (!documentId || !moduleOn) {
         return;
      }
      let cancelled = false;
      const t = setTimeout(() => {
         void (async () => {
            try {
               const items = await ANNManager.listAnnotations(AnnotationContextType.Servitut, documentId, organizationId);
               if (!cancelled) {
                  setQuickDownloadRecord(getQuickDownloadCandidate(items, datoloebenr));
               }
            } catch {
               if (!cancelled) {
                  setQuickDownloadRecord(null);
               }
            }
         })();
      }, 800);
      return () => {
         cancelled = true;
         clearTimeout(t);
      };
   }, [
      editorState.lastSaveTimestamp,
      editorState.contextType,
      editorState.identifier,
      documentId,
      datoloebenr,
      organizationId,
      moduleOn,
   ]);

   async function handleQuickDownload() {
      if (!quickDownloadRecord?.document?.s3Key || downloading) {
         return;
      }
      setDownloading(true);
      try {
         const { blob, fileName } = await downloadOverlayImage(quickDownloadRecord.document.s3Key);
         const resolvedDownloadName =
            downloadFileName ||
            quickDownloadRecord.document.filename ||
            fileName ||
            `document-${quickDownloadRecord.id}.pdf`;
         ExportData.downloadBlob(resolvedDownloadName, blob);
      } finally {
         setDownloading(false);
      }
   }

   if (!quickDownloadRecord) {
      return null;
   }

   const locale = Localization.getLocale().toLowerCase();
   const quickDownloadTitle = locale.startsWith("da")
      ? "Download servituttekst tidligere udklippet som annotation"
      : "Download servitut text previously extracted as annotation";

   return (
      <GlassButton
         className="tingbog-download-button"
         disabled={downloading}
         onClick={handleQuickDownload}
         title={downloading ? Localization.getText("Downloading...") : quickDownloadTitle}
         title-placement="top"
      >
         <MIBIcon icon="download" />
      </GlassButton>
   );
}
